/** Who may open an organisation's admin-only pages, as the server
 *  decides it: kept apart from the admin menu's model, because the shell
 *  loads this for every manager and the menu only on admin pages. */
import type { Me } from "@/api/types";

/** Whether the server's ``require_org_admin`` lets the viewer in for
 *  ``slug``: an admin of the organisation, or a platform admin (global
 *  admin or superadmin), who acts as the admin of every organisation
 *  whatever their own membership there. */
export function administersOrg(me: Me | undefined, slug: string | null): boolean {
  const scope = me?.admin_scope;
  if (!slug || !scope?.can_access_admin) return false;
  if (scope.is_global_admin || scope.is_superadmin) return true;
  return (scope.org_admin_slugs ?? []).includes(slug);
}

/** Whether the viewer may open ``slug``'s Billing page.
 *
 *  Every request behind it is require_org_admin, so a manager lands on
 *  a page of refusals; the admin menu hides Billing from them and the
 *  admin layout redirects a direct link to Overview. Surfaces outside
 *  the admin area that point at Billing for the active organisation
 *  (the app bar's credit chip, the setup checklist) ask this first, so
 *  they never send anyone into that redirect or a 403. It is stricter
 *  than the menu's rule (admin of any organisation), which only decides
 *  whether the page is listed. */
export function canOpenBilling(me: Me | undefined, slug: string | null): boolean {
  return Boolean(me?.features?.billing_enabled) && administersOrg(me, slug);
}

/** Whether the viewer may commit ``slug`` to paying: choose or change a
 *  paid plan, buy licences or credit packs, open the payment portal.
 *  Paid plans are self-serve, so this takes an ADMIN membership of the
 *  organisation — a platform admin sees the organisation's billing but
 *  can't buy for it (the server answers ``billing_self_serve_only``). */
export function selfServesBilling(me: Me | undefined, slug: string | null): boolean {
  if (!slug) return false;
  return (me?.admin_scope?.org_admin_slugs ?? []).includes(slug);
}
